/**
 * Member launcher and the Reward Program panel.
 * A hash such as #rewards_component_EArn scrolls to the matching Shopify
 * section on the rewards page (`id` ends with `__<hash>`).
 */
class MemberRewardPanel extends HTMLElement {
  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;
    this._launcher = this.querySelector('[data-member-reward-toggle]');
    this._dialog = this.querySelector('[data-member-reward-dialog]');
    this._onClick = this._onClick.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerOver = this._onPointerOver.bind(this);
    this._onPointerOut = this._onPointerOut.bind(this);
    this._onPoints = this._syncPoints.bind(this);
    this.addEventListener('click', this._onClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onToggle);
    document.addEventListener('pointerdown', this._onPointerDown);
    document.addEventListener('pointerover', this._onPointerOver);
    document.addEventListener('pointerout', this._onPointerOut);
    document.addEventListener('point_redemptions_updated', this._onPoints);
    document.addEventListener('points_balance_updated', this._onPoints);
    window.addEventListener('resize', this._onPoints);
    this._setOpen(false);
    this._syncPoints();
    this._scrollHash(window.location.hash);
  }

  disconnectedCallback() {
    this._unlockPage();
    this._restoreFloaters();
    this._mounted = false;
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onToggle);
    document.removeEventListener('pointerdown', this._onPointerDown);
    document.removeEventListener('pointerover', this._onPointerOver);
    document.removeEventListener('pointerout', this._onPointerOut);
    clearTimeout(this._hoverCloseTimer);
    document.removeEventListener('point_redemptions_updated', this._onPoints);
    document.removeEventListener('points_balance_updated', this._onPoints);
    window.removeEventListener('resize', this._onPoints);
  }

  /**
   * The launcher can live in the floating rail, outside this element.
   * @param {MouseEvent} event
   */
  _onToggle(event) {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target ? target.closest('[data-member-reward-toggle]') : null;
    if (!toggle || toggle.getAttribute('aria-controls') !== this._dialog?.id) return;
    this._launcher = toggle;
    if (this._swallowHoverClick(event)) return;
    this._setOpen(!this.classList.contains('is-open'));
  }

  /**
   * Desktop pointers open the card on hover and close it after leaving both
   * the icon and the card. The click that follows that same press would
   * toggle it shut, so ignore only that click.
   * @returns {boolean}
   */
  _canHoverOpen() {
    return window.matchMedia('(hover: hover) and (min-width: 768px)').matches;
  }

  /**
   * @param {EventTarget | null} node
   * @returns {boolean}
   */
  _isHoverZone(node) {
    if (!(node instanceof Element)) return false;
    const toggle = node.closest('[data-member-reward-toggle]');
    if (toggle && toggle.getAttribute('aria-controls') === this._dialog?.id) return true;
    const item = node.closest('[data-floating-rail-item]');
    if (item && this._launcher && item.contains(this._launcher)) return true;
    return !!(this._dialog && this._dialog.contains(node));
  }

  /**
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  _swallowHoverClick(event) {
    if (!this._canHoverOpen() || event.pointerType !== 'mouse') return false;
    if (!this._hoverOpenedAt || Date.now() - this._hoverOpenedAt > 400) return false;
    this._hoverOpenedAt = 0;
    return true;
  }

  /**
   * @param {PointerEvent} event
   */
  _onPointerOver(event) {
    if (!this._canHoverOpen()) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!this._isHoverZone(target)) return;
    clearTimeout(this._hoverCloseTimer);
    const toggle = target.closest('[data-member-reward-toggle]');
    if (toggle && toggle.getAttribute('aria-controls') === this._dialog?.id) this._launcher = toggle;
    if (this.classList.contains('is-open') || !toggle) return;
    this._hoverOpenedAt = Date.now();
    this._setOpen(true);
  }

  /**
   * @param {PointerEvent} event
   */
  _onPointerOut(event) {
    if (!this._canHoverOpen() || !this.classList.contains('is-open')) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!this._isHoverZone(target)) return;
    const next = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (this._isHoverZone(next)) return;
    clearTimeout(this._hoverCloseTimer);
    this._hoverCloseTimer = window.setTimeout(() => {
      if (!this._canHoverOpen()) return;
      this._setOpen(false);
    }, 200);
  }

  /**
   * @param {MouseEvent} event
   */
  _onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-member-reward-close]') || target.closest('[data-member-reward-backdrop]')) {
      this._setOpen(false);
      this._launcher?.focus();
      return;
    }
    const link = target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    const hash = new URL(link.href, window.location.href).hash;
    if (hash && this._scrollHash(hash)) {
      event.preventDefault();
      this._setOpen(false);
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  _onKeydown(event) {
    if (!this.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      this._setOpen(false);
      this._launcher?.focus();
      return;
    }
    const scrollsPage = [' ', 'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!scrollsPage.includes(event.key)) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target && (target.closest('input, textarea, select') || this._dialog?.contains(target))) return;
    event.preventDefault();
  }

  /**
   * @param {PointerEvent} event
   */
  _onPointerDown(event) {
    if (!this.classList.contains('is-open')) return;
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target ? target.closest('[data-member-reward-toggle]') : null;
    if (toggle && toggle.getAttribute('aria-controls') === this._dialog?.id) return;
    if (target && this.contains(target)) return;
    this._setOpen(false);
  }

  /**
   * @param {boolean} open
   */
  _setOpen(open) {
    this.classList.toggle('is-open', open);
    this._launcher?.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (this._dialog) this._dialog.inert = !open;
    if (open) {
      this._syncPoints();
      this._sinkFloaters();
      this._lockPage();
    } else {
      this._unlockPage();
      this._restoreFloaters();
    }
  }

  /**
   * Stop the page behind the card. The card keeps its own overflow scroll.
   * Listeners are non-passive so a wheel or touch on the page can be cancelled.
   */
  _lockPage() {
    if (this._pageLocked) return;
    this._pageLocked = true;
    this._onLockWheel = (event) => this._onPageWheel(event);
    this._onLockTouch = (event) => this._onPageTouch(event);
    document.body.classList.add('member-reward-panel-lock');
    document.addEventListener('wheel', this._onLockWheel, { passive: false });
    document.addEventListener('touchmove', this._onLockTouch, { passive: false });
  }

  _unlockPage() {
    if (!this._pageLocked) return;
    this._pageLocked = false;
    document.body.classList.remove('member-reward-panel-lock');
    document.removeEventListener('wheel', this._onLockWheel);
    document.removeEventListener('touchmove', this._onLockTouch);
  }

  /**
   * @param {WheelEvent} event
   * @returns {boolean}
   */
  _dialogCanConsume(event) {
    const target = event.target instanceof Element ? event.target : null;
    const dialog = this._dialog;
    if (!target || !dialog?.contains(target)) return false;
    if (dialog.scrollHeight <= dialog.clientHeight + 1) return false;
    if (event.deltaY < 0) return dialog.scrollTop > 0;
    if (event.deltaY > 0) return dialog.scrollTop + dialog.clientHeight < dialog.scrollHeight - 1;
    return true;
  }

  /**
   * @param {WheelEvent} event
   */
  _onPageWheel(event) {
    if (!this.classList.contains('is-open') || this._dialogCanConsume(event)) return;
    event.preventDefault();
  }

  /**
   * @param {TouchEvent} event
   */
  _onPageTouch(event) {
    if (!this.classList.contains('is-open')) return;
    const target = event.target instanceof Element ? event.target : null;
    const dialog = this._dialog;
    if (target && dialog?.contains(target) && dialog.scrollHeight > dialog.clientHeight + 1) return;
    event.preventDefault();
  }

  /**
   * AccessPro paints its own fixed layer above the sheet. While the mobile
   * sheet is open, lower that layer without moving the icon.
   */
  _sinkFloaters() {
    this._restoreFloaters();
    if (document.querySelector('.floating-rail')) return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    this._floaterRestore = ['#apMenuIconStroke', '#asw-icon'].flatMap((selector) => {
      const node = document.querySelector(selector);
      if (!node || this.contains(node)) return [];
      let target = node;
      let current = node;
      while (current && current !== document.body) {
        const style = getComputedStyle(current);
        if (style.position !== 'static' && style.zIndex !== 'auto') target = current;
        current = current.parentElement;
      }
      const previous = target.style.zIndex;
      const priority = target.style.getPropertyPriority('z-index');
      target.style.setProperty('z-index', '1', 'important');
      return [{ node: target, previous, priority }];
    });
  }

  _restoreFloaters() {
    (this._floaterRestore || []).forEach(({ node, previous, priority }) => {
      if (previous) node.style.setProperty('z-index', previous, priority);
      else node.style.removeProperty('z-index');
    });
    this._floaterRestore = [];
  }

  _syncPoints() {
    const node = this.querySelector('[data-member-reward-points]');
    const raw = window.localStorage.getItem('points_balance');
    if (raw == null || raw === '') {
      delete this.dataset.points;
      this.classList.remove('is-warning-tall');
      this._loadPoints();
      return;
    }
    const amount = Number(String(raw).replace(/,/g, ''));
    if (node) node.textContent = raw;
    if (Number.isNaN(amount)) return;
    const threshold = Number(this.dataset.pointsThreshold || '700');
    this.dataset.points = amount < threshold ? 'low' : 'ok';
    this._fitWarning();
  }

  /**
   * Points normally arrive only after some other Yotpo call fills localStorage.
   * On a cache miss, ask the same public customer endpoint the widget uses.
   */
  _loadPoints() {
    if (this.dataset.memberState !== 'member' || this._pointsRequest) return;
    const email = this.dataset.customerEmail;
    const customerId = this.dataset.customerId;
    const guid = this._yotpoGuid();
    if (!email || !customerId || !guid) return;
    const url = new URL('https://loyalty.yotpo.com/api/public/v1/customer_details');
    url.searchParams.set('guid', guid);
    this._pointsRequest = fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: {
        'X-CUSTOMER-EMAIL': email,
        'X-CUSTOMER-STORE-ACCOUNT-ID': customerId
      }
    }).then(async (response) => {
      if (!response.ok) return;
      const body = await response.json();
      const balance = body?.points_balance ?? body?.data?.points_balance;
      if (balance == null || balance === '') return;
      window.localStorage.setItem('points_balance', String(balance));
      document.dispatchEvent(new CustomEvent('points_balance_updated'));
    }).catch(() => {});
  }

  /**
   * The loader URL is `.../loader/<guid>`. That guid is the public store key.
   * @returns {string}
   */
  _yotpoGuid() {
    const script = document.querySelector('script[src*="cdn-widgetsrepository.yotpo.com/v1/loader/"]');
    const match = script?.src.match(/\/loader\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  _fitWarning() {
    const warning = this.querySelector('[data-points-warning]');
    if (!warning || this.dataset.points !== 'low') {
      this.classList.remove('is-warning-tall');
      return;
    }
    const line = parseFloat(getComputedStyle(warning).lineHeight) || 20;
    this.classList.toggle('is-warning-tall', warning.scrollHeight > line + 2);
  }

  /**
   * @param {string} hash
   * @returns {boolean}
   */
  _scrollHash(hash) {
    const id = (hash || '').replace(/^#/, '');
    if (!id) return false;
    const target = document.getElementById(id) || document.querySelector(`[id$="__${CSS.escape(id)}"]`);
    if (!(target instanceof HTMLElement)) return false;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }
}

if (!customElements.get('member-reward-panel')) {
  customElements.define('member-reward-panel', MemberRewardPanel);
}
